// Public facts about the business, shown on the website and in the Terms and Privacy Policy.
//
// The backend's .env is the single source of truth (see "Business identity" in
// backend/BACKEND_SETUP.md) — loadSiteConfig() fetches it once from GET /api/site-config and fills
// in the fields below before the app renders (see App.js). Nothing here is invented: a field that
// comes back empty stays empty, and pages leave it out rather than showing a placeholder.
//
// REACT_APP_* build-time values are kept only as a fallback for local development without a
// backend running, and are overwritten the moment loadSiteConfig() succeeds.

const env = process.env;

const clean = (value) => (value || '').trim();

// A plain mutable object, not a fresh one per call: every file that does `import { SITE }`
// shares this same reference, so mutating its fields in loadSiteConfig() below updates it
// everywhere at once, with no need for React context or prop drilling.
export const SITE = {
  name: 'TenantVerify SA',
  tagline: 'Consent-based tenant verification for South Africa',

  // Who is behind the service. Required for the Terms and Privacy Policy to say who the "responsible party" is.
  legalName: clean(env.REACT_APP_LEGAL_NAME),
  registrationNumber: clean(env.REACT_APP_REGISTRATION_NUMBER),
  address: clean(env.REACT_APP_ADDRESS),

  // How people reach us.
  supportEmail: clean(env.REACT_APP_SUPPORT_EMAIL),
  phone: clean(env.REACT_APP_SUPPORT_PHONE),

  // POPIA: the Information Officer handles privacy requests and complaints. Defaults to the support address.
  informationOfficerName: clean(env.REACT_APP_INFORMATION_OFFICER_NAME),
  informationOfficerEmail: clean(env.REACT_APP_INFORMATION_OFFICER_EMAIL) || clean(env.REACT_APP_SUPPORT_EMAIL),

  // Bump when the Terms or Privacy Policy change. Keep in step with TERMS_VERSION on the server, which records
  // which version each person accepted at registration.
  termsVersion: '2026-09-22',
  termsUpdated: '22 September 2026',

  // How long personal data is kept. Must match RETENTION_APPLICATION_MONTHS and the retention schedule on the server,
  // which enforces them (php artisan tenantverify:apply-retention).
  retentionMonths: 12,
  auditLogYears: 5,
  disputeYears: 3
};

// Fetches the real values from the backend and fills them into SITE in place. Called once at
// startup (see App.js); safe to call more than once if you ever need to refresh it. Failures are
// swallowed on purpose — if the API is briefly unreachable, pages fall back to whatever SITE
// already had (the build-time values, or nothing) rather than crashing the whole app over a
// business-details fetch.
export const loadSiteConfig = async () => {
  try {
    // Lazy import: avoids a circular dependency, since services/publicService.js doesn't need
    // anything from this file.
    const { default: publicService } = await import('../services/publicService');
    const data = await publicService.getSiteConfig();

    SITE.legalName = clean(data.legal_name) || SITE.legalName;
    SITE.registrationNumber = clean(data.registration_number) || SITE.registrationNumber;
    SITE.address = clean(data.address) || SITE.address;
    SITE.supportEmail = clean(data.support_email) || SITE.supportEmail;
    SITE.phone = clean(data.support_phone) || SITE.phone;
    SITE.informationOfficerName = clean(data.information_officer_name) || SITE.informationOfficerName;
    SITE.informationOfficerEmail = clean(data.information_officer_email) || SITE.informationOfficerEmail;
  } catch (e) {
    // Development without a backend running, or a transient network error — either way, the app
    // should still render with whatever SITE already had.
  }
};

// The name to use where a legal entity is meant ("the responsible party is ...").
export const legalEntity = () => SITE.legalName || SITE.name;

// What is still missing before a real launch. Shown in development only, never on the live site.
export const missingLaunchSettings = () => {
  const missing = [];
  if (!SITE.legalName) missing.push('LEGAL_NAME (the registered company name, in the backend .env)');
  if (!SITE.registrationNumber) missing.push('REGISTRATION_NUMBER (backend .env)');
  if (!SITE.address) missing.push('ADDRESS (physical address, backend .env)');
  if (!SITE.supportEmail) missing.push('SUPPORT_EMAIL (backend .env)');
  if (!SITE.informationOfficerName) missing.push('INFORMATION_OFFICER_NAME (backend .env)');
  return missing;
};
